import { z } from 'zod';
/**
 * Обновление справочника людей из Alfa People (docs/modules/alfa-people-sync.md).
 *
 * Выгрузку портала забирает личное расширение Chrome (`extensions/alfa-people`)
 * с куками браузера или человек загружает JSON-файл руками. Страница Lens
 * разбирает ответ портала этим модулем, сводит строки «человек в команде» в
 * одного человека и отправляет на сервер уже нормализованный список
 * (`AlfaPeoplePerson[]`). Сравнение со справочником и применение — на сервере.
 */
// ─── Поля, которые даёт портал ───────────────────────────────────────────
// key — стабильный идентификатор в настройке сопоставления; label — как
// показывать в интерфейсе. `login` не настраивается: это ключ поиска
// человека, он всегда пишется в ключевую колонку справочника.
export const ALFA_PEOPLE_FIELDS = [
    { key: 'fio', label: 'ФИО' },
    { key: 'pernr', label: 'Табельный номер' },
    { key: 'position', label: 'Должность' },
    { key: 'employment', label: 'Тип занятости' },
    { key: 'vendor', label: 'Не в штате (да/нет)' },
    { key: 'company', label: 'Юрлицо вендора' },
    { key: 'accessStatus', label: 'Статус доступа' },
    { key: 'team', label: 'Команда' },
    { key: 'teams', label: 'Все команды' },
    { key: 'share', label: 'Доля в команде, %' },
    { key: 'roleGuess', label: 'Роль по должности (подсказка)' },
];
export const AlfaPeopleFieldKeySchema = z.enum(ALFA_PEOPLE_FIELDS.map((f) => f.key));
// ─── Нормализованный человек ─────────────────────────────────────────────
export const AlfaPeoplePersonSchema = z.object({
    login: z.string().min(1).max(100),
    fio: z.string().max(300).nullable(),
    pernr: z.string().max(50).nullable(),
    position: z.string().max(300).nullable(),
    employment: z.string().max(200).nullable(),
    vendor: z.boolean().nullable(),
    company: z.string().max(300).nullable(),
    accessStatus: z.string().max(200).nullable(),
    team: z.string().max(300).nullable(),
    teams: z.array(z.string().max(300)).max(50),
    share: z.number().nullable(),
    roleGuess: z.array(z.string().max(50)).max(10),
});
// ─── Настройка у справочника ─────────────────────────────────────────────
// mode: 'update' — значение портала заменяет значение в справочнике при каждом
// обновлении; 'create' — пишется только при заведении нового человека (ручные
// правки потом не трогаются).
export const DictionarySyncModeSchema = z.enum(['update', 'create']);
export const DictionarySyncMappingSchema = z.object({
    field: AlfaPeopleFieldKeySchema,
    columnId: z.string().uuid(),
    mode: DictionarySyncModeSchema,
});
export const DictionarySyncConfigSchema = z.object({
    // Табельные номера руководителей: портал отдаёт подчинённых одного
    // руководителя за запрос, людей из нескольких веток собираем по списку.
    managers: z
        .array(z
        .string()
        .trim()
        .regex(/^\d{1,20}$/, 'Табельный номер — только цифры'))
        .max(20),
    mapping: z.array(DictionarySyncMappingSchema).max(50),
    // Колонка «да/нет», которую снимаем у тех, кого больше нет в портале.
    // Записи не удаляем: на них ссылаются карточки.
    activeColumnId: z.string().uuid().nullable(),
});
// ─── Сравнение ───────────────────────────────────────────────────────────
export const DictionarySyncPreviewRequestSchema = z.object({
    people: z.array(AlfaPeoplePersonSchema).max(5000),
});
export const DictionarySyncValueChangeSchema = z.object({
    columnId: z.string(),
    columnName: z.string(),
    from: z.unknown(),
    to: z.unknown(),
    fromLabel: z.string(),
    toLabel: z.string(),
});
export const DictionarySyncPreviewSchema = z.object({
    sourceCount: z.number().int(),
    create: z.array(z.object({
        key: z.string(),
        label: z.string(),
        changes: z.array(DictionarySyncValueChangeSchema),
    })),
    update: z.array(z.object({
        entryId: z.string(),
        key: z.string(),
        label: z.string(),
        changes: z.array(DictionarySyncValueChangeSchema),
    })),
    // Есть в справочнике, нет в выгрузке. Уже снятые с «активен» сюда не попадают.
    missing: z.array(z.object({ entryId: z.string(), key: z.string(), label: z.string() })),
    unchanged: z.number().int(),
    warnings: z.array(z.string()),
});
// ─── Применение ──────────────────────────────────────────────────────────
// Сервер заново сравнивает присланный список со справочником и применяет
// только отмеченное: так не нужно хранить результат сравнения между запросами.
export const DictionarySyncApplyRequestSchema = z.object({
    people: z.array(AlfaPeoplePersonSchema).max(5000),
    create: z.array(z.string()).max(5000),
    update: z.array(z.string().uuid()).max(5000),
    deactivate: z.array(z.string().uuid()).max(5000),
});
export const DictionarySyncRunSchema = z.object({
    id: z.string().uuid(),
    dictionaryId: z.string().uuid(),
    source: z.string(),
    user: z.object({ id: z.string(), login: z.string(), displayName: z.string() }).nullable(),
    createdAt: z.string(),
    sourceCount: z.number().int(),
    created: z.number().int(),
    updated: z.number().int(),
    deactivated: z.number().int(),
});
export const DictionarySyncRunDetailSchema = DictionarySyncRunSchema.extend({
    changes: z.object({
        create: DictionarySyncPreviewSchema.shape.create,
        update: DictionarySyncPreviewSchema.shape.update,
        deactivate: DictionarySyncPreviewSchema.shape.missing,
    }),
});
export const DictionarySyncStateSchema = z.object({
    config: DictionarySyncConfigSchema.nullable(),
    runs: z.array(DictionarySyncRunSchema),
});
// ─── Разбор ответа портала ───────────────────────────────────────────────
/** Адрес выгрузки «мои подчинённые» для руководителя с табельным `cpId`. */
export function alfaPeopleExportUrl(cpId) {
    return `https://alfapeople.alfabank.ru/manager-account/api/v1/in-team?cpId=${encodeURIComponent(cpId)}&view=InTeamMySubordinate`;
}
/** Логин без домена (`MOSCOW\U_ABC` → `U_ABC`). */
export function stripLoginDomain(login) {
    const s = login.trim();
    const i = s.lastIndexOf('\\');
    return i >= 0 ? s.slice(i + 1) : s;
}
/** Ключ сравнения логинов: без домена и без учёта регистра. */
export function loginMatchKey(login) {
    return stripLoginDomain(login).toUpperCase();
}
function str(v, max = 300) {
    if (v == null)
        return null;
    if (typeof v === 'string' || typeof v === 'number') {
        const s = String(v).trim();
        return s ? s.slice(0, max) : null;
    }
    if (typeof v === 'object') {
        const o = v;
        return str(o.name ?? o.text ?? o.value ?? null, max);
    }
    return null;
}
/**
 * Достаёт список команд из ответа портала. Портал отдаёт массив команд,
 * `{ data: [...] }` или объект, значения которого — команды; у каждой
 * команды обязан быть массив `navEmployee`.
 */
export function parseAlfaPeopleExport(raw) {
    let teams = raw;
    if (teams && typeof teams === 'object' && !Array.isArray(teams)) {
        const o = teams;
        teams = Array.isArray(o.data) ? o.data : Object.values(o);
    }
    if (!Array.isArray(teams) ||
        !teams.every((t) => t && typeof t === 'object' && Array.isArray(t.navEmployee))) {
        throw new Error('Это не выгрузка Alfa People: нет команд с полем navEmployee');
    }
    return teams;
}
// Юрлицо вендора: портал кладёт его в поле с заранее неизвестным именем,
// поэтому ищем среди полей «похожих по названию» значение с формой юрлица.
const COMPANY_KEY_RE = /org|company|firm|vendor|contractor|employer|partner|legal|jur|entity/i;
// `\b` в JS не видит границу кириллического слова — граница задана явно.
const LEGAL_FORM_RE = /^(ооо|оао|зао|пао|нао|ао|ип)(?=[\s«"]|$)/i;
function companyOf(row) {
    for (const [k, v] of Object.entries(row)) {
        if (k === 'orgTypeName' || !COMPANY_KEY_RE.test(k))
            continue;
        const s = str(v);
        if (s && LEGAL_FORM_RE.test(s.replace(/^[«"']/, '')))
            return s;
    }
    return null;
}
// participationPercent приходит долей («0.30») или процентом («30»).
function shareOf(v) {
    const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v.replace(',', '.')) : NaN;
    if (!Number.isFinite(n) || n < 0)
        return null;
    return Math.round((n <= 1 ? n * 100 : n) * 100) / 100;
}
// Подсказка роли по должности: кандидаты по порядку, на сервере берётся
// первый, который есть среди вариантов колонки.
const ROLE_RULES = [
    [/технический лидер|техлид|tech\s*lead/i, ['TechLead', 'Tech Lead']],
    [/руководитель команды|тимлид|team\s*lead/i, ['TeamLead', 'Team Lead']],
    [/тестир|автоматиз|\bqa\b/i, ['QA', 'AT']],
    [/бизнес-аналит|бизнес аналит/i, ['BA', 'SA']],
    [/аналит/i, ['SA']],
    [/front|фронт/i, ['Front']],
    [/back|бэк|java|разработчик|программист/i, ['Back']],
    [/руководитель|менеджер|директор|начальник/i, ['Менеджмент']],
];
export function guessRole(position) {
    if (!position)
        return [];
    for (const [re, roles] of ROLE_RULES)
        if (re.test(position))
            return roles;
    return [];
}
/**
 * Сводит строки «человек в команде» в одного человека на логин. Вакансии и
 * строки без логина пропускаются. Основная команда — с наибольшей долей.
 */
export function normalizeAlfaPeople(teams) {
    const byLogin = new Map();
    for (const team of teams) {
        const teamName = str(team.teamName);
        for (const r of team.navEmployee) {
            if (!r || typeof r !== 'object')
                continue;
            const row = r;
            const fio = str(row.fio);
            if (fio === 'Вакансия')
                continue;
            const rawLogin = str(row.domainUser, 100);
            if (!rawLogin)
                continue;
            const login = stripLoginDomain(rawLogin);
            if (!login)
                continue;
            const share = shareOf(row.participationPercent);
            const key = loginMatchKey(login);
            let p = byLogin.get(key);
            if (!p) {
                const position = str(row.plansName);
                const employment = str(row.orgTypeName, 200);
                p = {
                    login,
                    fio,
                    pernr: str(row.pernr, 50),
                    position,
                    employment,
                    vendor: employment ? !/штат/i.test(employment) : null,
                    company: companyOf(row),
                    accessStatus: str(row.status?.text ?? row.status, 200),
                    team: null,
                    teams: [],
                    share: null,
                    roleGuess: guessRole(position),
                    _best: -1,
                };
                byLogin.set(key, p);
            }
            if (teamName && !p.teams.includes(teamName))
                p.teams.push(teamName);
            const weight = share ?? 0;
            if (teamName && weight > p._best) {
                p._best = weight;
                p.team = teamName;
                p.share = share;
            }
        }
    }
    return [...byLogin.values()].map(({ _best: _drop, ...p }) => p);
}
